import { useRef, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { useLoginForm } from '@/hooks/useLoginForm'
import { PhoneInput } from '@/components/auth/PhoneInput'
import { OTPInput } from '@/components/auth/OTPInput'
import { AlertMessage } from '@/components/auth/AlertMessage'
import { RegistrationOptions } from '@/components/auth/RegistrationOptions'
import { LoginHeader } from '@/components/auth/LoginHeader'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'

/**
 * Login Page Component
 * 
 * Senior-level implementation with:
 * - Separation of concerns (custom hooks, reusable components)
 * - Proper validation and error handling
 * - Accessibility features (ARIA labels, keyboard navigation)
 * - Type safety
 * - Performance optimizations
 */
export function LoginPage() {
  const { t } = useLocalization()
  const {
    phone,
    otp,
    step,
    error,
    successMessage,
    isLoading,
    isPhoneValid,
    isOTPValid,
    setPhone,
    setOtp,
    handlePhoneSubmit,
    handleOTPSubmit,
    handleResendOTP,
    handleBack,
  } = useLoginForm()

  const phoneInputRef = useRef<HTMLInputElement>(null)
  const otpInputRef = useRef<HTMLInputElement>(null)
  
  // Track if auto-submit should be disabled (after an error)
  const [autoSubmitDisabled, setAutoSubmitDisabled] = useState(false)

  // Auto-focus the active step's input
  useEffect(() => {
    if (step === 'phone' && phoneInputRef.current) {
      phoneInputRef.current.focus()
      // Reset auto-submit when going back to phone step
      setAutoSubmitDisabled(false)
    } else if (step === 'otp' && otpInputRef.current) {
      otpInputRef.current.focus()
    }
  }, [step])

  // Disable auto-submit when there's an error
  useEffect(() => {
    if (error && step === 'otp') {
      setAutoSubmitDisabled(true)
    }
  }, [error, step])

  // Auto-submit OTP when 6 digits are entered (only if not disabled)
  useEffect(() => {
    if (step === 'otp' && isOTPValid && !isLoading && !autoSubmitDisabled) {
      // Small delay to allow user to see the last digit
      const timer = setTimeout(() => {
        handleOTPSubmit(new Event('submit') as any)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [otp, step, isOTPValid, isLoading, handleOTPSubmit, autoSubmitDisabled])

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-[#131375]/10 px-3 sm:px-4 py-4 sm:py-8 relative overflow-x-hidden overflow-y-auto">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -end-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -start-40 w-80 h-80 bg-blue-400/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Language Switcher - Top Right */}
        <div className="flex justify-end mb-3 sm:mb-6" aria-label={t('auth.languageSelector')}>
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm p-1 border border-gray-100/50">
            <LanguageSwitcher />
          </div>
        </div>

        <Card className="shadow-2xl border border-gray-100/50 bg-white/80 backdrop-blur-xl transition-all duration-300 hover:shadow-3xl">
          <LoginHeader step={step} phone={phone} />

          <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 pb-6 sm:pb-8">
            {/* Phone Step */}
            {step === 'phone' && (
              <form
                onSubmit={handlePhoneSubmit}
                noValidate
                className="space-y-4 sm:space-y-5"
                aria-label={t('auth.phoneLoginForm')}
              >
                <PhoneInput
                  ref={phoneInputRef}
                  value={phone}
                  onChange={setPhone}
                  error={error}
                  disabled={isLoading}
                  aria-label={t('auth.phoneNumber')}
                  aria-describedby={error ? 'phone-error' : undefined}
                />

                {error && (
                  <AlertMessage
                    type="error"
                    message={error}
                    aria-live="assertive"
                    id="phone-error"
                    key="phone-error"
                  />
                )}

                <Button
                  type="submit"
                  className="w-full h-11 sm:h-12 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:hover:scale-100"
                  disabled={isLoading || !isPhoneValid}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 me-2 animate-spin" aria-hidden="true" />
                      {t('auth.sendingOtp')}
                    </>
                  ) : (
                    t('auth.sendVerificationCode')
                  )}
                </Button>
              </form>
            )}

            {/* OTP Step */}
            {step === 'otp' && (
              <form
                onSubmit={handleOTPSubmit}
                noValidate
                className="space-y-4 sm:space-y-6"
                aria-label={t('auth.otpVerificationForm')}
              >
                <OTPInput
                  ref={otpInputRef}
                  value={otp}
                  onChange={setOtp}
                  disabled={isLoading}
                  error={!!error && !successMessage}
                  aria-label={t('auth.otpCode')}
                  aria-describedby={error ? 'otp-error' : undefined}
                />

                {error && (
                  <AlertMessage
                    type="error"
                    message={error}
                    aria-live="assertive"
                    id="otp-error"
                    key="otp-error"
                  />
                )}

                {successMessage && (
                  <AlertMessage
                    type="success"
                    message={successMessage}
                    aria-live="polite"
                  />
                )}

                <Button
                  type="submit"
                  className="w-full h-11 sm:h-12 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:hover:scale-100"
                  disabled={isLoading || !isOTPValid}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 me-2 animate-spin" aria-hidden="true" />
                      {t('auth.verifying')}
                    </>
                  ) : (
                    t('auth.verifyCode')
                  )}
                </Button>

                {/* Resend OTP Link */}
                <div className="text-center pt-1 sm:pt-2">
                  <Button
                    type="button"
                    variant="link"
                    className="text-xs sm:text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200"
                    onClick={handleResendOTP}
                    disabled={isLoading}
                  >
                    {t('auth.didntReceiveCode')}{' '}
                    <span className="underline underline-offset-2">{t('auth.resendOtp')}</span>
                  </Button>
                </div>

                {/* Back Button */}
                <Button
                  type="button"
                  variant="ghost"
                  className="group w-full text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 disabled:text-gray-400 transition-all duration-200 rounded-lg"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  <ArrowLeft className="w-4 h-4 me-2 transition-transform group-hover:-translate-x-1" aria-hidden="true" />
                  {t('auth.backToPhoneLogin')}
                </Button>
              </form>
            )}

            {/* Registration Options */}
            {step === 'phone' && <RegistrationOptions />}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
